import React, { useEffect, useMemo, useState } from "react";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import CheckIcon from "@mui/icons-material/Check";
import { Button, CircularProgress } from "@mui/material";
import NitroService from "../../../utils/nitro.service";
import { RailStationAddCarrierPanelProps } from "./IRailStationLookup";
import "./RailStationAddCarrierPanel.css";

const RailStationAddCarrierPanel: React.FC<RailStationAddCarrierPanelProps> = ({
  open,
  initialSelectedCarriers,
  onBack,
  onApplySelected,
  onClose,
}) => {
  const [searchValue, setSearchValue] = useState("");
  const [selectedSet, setSelectedSet] = useState<Set<string>>(new Set());
  const [carrierOptions, setCarrierOptions] = useState<string[]>([]);
  const [loadingCarriers, setLoadingCarriers] = useState(false);
  const [carriersError, setCarriersError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setSelectedSet(new Set());
    setSearchValue("");
    setCarriersError(null);

    const fetchCarriers = async () => {
      setLoadingCarriers(true);
      try {
        const records = await NitroService.getCarriers();
        const names = records
          .map((r: any) =>
            typeof r === "string" ? r : r?.name || r?.carrier || "",
          )
          .filter(Boolean) as string[];
        const merged = Array.from(
          new Set([...names, ...initialSelectedCarriers]),
        );
        setCarrierOptions(merged);
      } catch (err) {
        console.error("Error fetching carriers:", err);
        setCarriersError("Failed to load carriers. Please try again.");
        setCarrierOptions(Array.from(new Set([...initialSelectedCarriers])));
      } finally {
        setLoadingCarriers(false);
      }
    };

    fetchCarriers();
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const filteredOptions = useMemo(() => {
    const query = searchValue.trim().toLowerCase();
    if (!query) return carrierOptions;
    return carrierOptions.filter((carrier) =>
      carrier.toLowerCase().includes(query),
    );
  }, [carrierOptions, searchValue]);

  const hasCheckedFilteredOptions = useMemo(() => {
    return filteredOptions.some((carrier) => selectedSet.has(carrier));
  }, [filteredOptions, selectedSet]);

  const toggleCarrier = (carrier: string) => {
    setSelectedSet((prev) => {
      const next = new Set(prev);
      if (next.has(carrier)) {
        next.delete(carrier);
      } else {
        next.add(carrier);
      }
      return next;
    });
  };

  if (!open) return null;

  return (
    <div className="rail-add-carrier-overlay">
      <div className="rail-add-carrier-panel">
        <div className="rail-add-carrier-header">
          <button
            type="button"
            className="rail-add-carrier-icon-btn"
            onClick={() => onBack(Array.from(selectedSet))}
            aria-label="Back"
          >
            <ArrowBackIcon fontSize="small" />
          </button>

          <div className="rail-add-carrier-title">Add Carrier</div>

          <button
            type="button"
            className="rail-add-carrier-icon-btn"
            onClick={onClose}
            aria-label="Close"
          >
            <CloseIcon fontSize="small" />
          </button>
        </div>

        <div className="rail-add-carrier-search-wrap">
          <SearchIcon sx={{ fontSize: 19, color: "#94A3B8" }} />
          <input
            id="rail-add-carrier-search-input-id"
            className="rail-add-carrier-search-input"
            value={searchValue}
            onChange={(event) => setSearchValue(event.target.value)}
            placeholder="Search carriers..."
          />
        </div>

        <div className="rail-add-carrier-list">
          {loadingCarriers && (
            <div className="rail-add-carrier-loading">
              <CircularProgress size={24} />
              <span>Loading carriers...</span>
            </div>
          )}
          {!loadingCarriers && carriersError && (
            <div className="rail-add-carrier-error">{carriersError}</div>
          )}
          {!loadingCarriers && filteredOptions.map((carrier) => {
            const isChecked = selectedSet.has(carrier);
            return (
              <button
                key={carrier}
                type="button"
                className="rail-add-carrier-row"
                onClick={() => toggleCarrier(carrier)}
              >
                <span className="rail-add-carrier-row-text">{carrier}</span>
                <span
                  className={`rail-add-carrier-row-checkbox ${
                    isChecked ? "checked" : ""
                  }`}
                  aria-hidden
                >
                  {isChecked ? <CheckIcon sx={{ fontSize: 16 }} /> : null}
                </span>
              </button>
            );
          })}
        </div>

        {hasCheckedFilteredOptions && (
          <div
            id="rail-add-carrier-footer-id"
            className="rail-add-carrier-footer"
          >
            <Button
              id="add-selected-carriers-button-id"
              variant="contained"
              className="rail-add-carrier-submit-btn"
              onClick={() => onApplySelected(Array.from(selectedSet))}
            >
              Add Selected Carriers
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default RailStationAddCarrierPanel;
